import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useIntersectionObserver } from "../hooks/useIntersectionObserver";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const FooterContainer = styled.footer`
  padding: 3rem 2rem;
  background: ${({ theme }) => theme.surface};
  margin-top: auto;
  position: relative;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0;
  animation: ${fadeIn} ${({ isVisible }) => (isVisible ? "0.6s" : "0s")}
    ease-out forwards;
`;

const SocialLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;

  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const SocialItem = styled.li`
  opacity: 0;
  animation: ${slideUp} 0.6s ease-out forwards;
  animation-delay: ${({ index }) => index * 0.1}s;
`;

const SocialLink = styled.a`
  color: ${({ theme }) => theme.text.secondary};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1.2rem;
  border-radius: 8px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.primary};
    opacity: 0;
    transform: translateY(100%);
    transition: all 0.3s ease;
    z-index: -1;
  }

  &:hover {
    color: ${({ theme }) => theme.button.text};
    transform: translateY(-2px);

    &:before {
      opacity: 0.1;
      transform: translateY(0);
    }

    i {
      animation: ${pulse} 0.6s ease-in-out;
    }
  }

  i {
    font-size: 1.5rem;
    transition: transform 0.3s ease;
  }

  .label {
    font-weight: 500;

    @media (max-width: 480px) {
      display: none;
    }
  }
`;

const ScrollToTop = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem; // Back to original position
  background: ${({ theme }) => theme.button.background};
  color: ${({ theme }) => theme.button.text};
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transform: ${({ visible }) =>
    visible ? "translateY(0)" : "translateY(20px)"};
  pointer-events: ${({ visible }) => (visible ? "all" : "none")};

  &:hover {
    background: ${({ theme }) => theme.button.hover};
    transform: ${({ visible }) =>
      visible ? "translateY(-5px)" : "translateY(20px)"};
  }

  @media (max-width: 768px) {
    bottom: 1rem;
    right: 1.5rem;
  }
`;

const Copyright = styled.p`
  color: ${({ theme }) => theme.text.light};
  text-align: center;
  margin-top: 2rem;
  font-size: 0.9rem;
  opacity: 0;
  animation: ${fadeIn} 0.6s ease-out 0.6s forwards;
`;

const Footer = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [footerRef, isFooterVisible] = useIntersectionObserver({
    threshold: 0.1,
  });

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const socialLinks = [
    {
      name: "Github",
      icon: "icon brands fa-github",
      url: "https://github.com/sanaanymoe",
    },
    {
      name: "LinkedIn",
      icon: "icon brands fa-linkedin",
      url: "https://www.linkedin.com/in/mohamed-sanaany-52112b1b2/",
    },
  ];

  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer ref={footerRef}>
      <FooterContent isVisible={isFooterVisible}>
        <SocialLinks>
          {socialLinks.map((link, index) => (
            <SocialItem key={link.name} index={index}>
              <SocialLink
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className={link.icon}></i>
                <span className="label">{link.name}</span>
              </SocialLink>
            </SocialItem>
          ))}
        </SocialLinks>
        <Copyright>
          © {currentYear} Mohamed Sanaany. All rights reserved.
        </Copyright>
      </FooterContent>
      <ScrollToTop
        visible={showScrollTop}
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
        <i className="fas fa-arrow-up"></i>
      </ScrollToTop>
    </FooterContainer>
  );
};

export default Footer;
